import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Home, Info, CreditCard, Layers, Briefcase, Send, Bot, Sparkles, Layers3, Boxes, ChevronDown, Sun, Moon } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAdmin } from "../context/AdminContext";

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  hasDropdown?: boolean;
}

export default function Navbar() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [isAiOpen] = useState<boolean>(false); 
  const [isEnquiryOpen, setIsEnquiryOpen] = useState<boolean>(false);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [showMobileTabs, setShowMobileTabs] = useState<boolean>(true);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState<boolean>(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState<boolean>(false);

  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const lastScrollY = useRef<number>(0);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isPortfolioPage = location.pathname.toLowerCase() === "/portfolio";
  const isProductDetailPage = location.pathname.toLowerCase().startsWith("/ourproducts/app/");
  const shouldHideNavbarOnScroll = isPortfolioPage && isScrolled;

  const serviceSubmenu = [
    {
      name: "Website Development",
      path: "/services/website-development",
      icon: "🌐",
      desc: "React, Next.js, WordPress & Custom Web Apps"
    },
    {
      name: "App Development",
      path: "/services/app-development",
      icon: "📱",
      desc: "iOS (Swift), Android (Kotlin) & Cross-Platform"
    },
    {
      name: "Digital Marketing & SEO",
      path: "/services/digital-marketing-seo",
      icon: "🚀",
      desc: "SEO Optimization & Performance Ads"
    },
    {
      name: "E-Commerce Solutions",
      path: "/services/ecommerce-solutions",
      icon: "🛒",
      desc: "Shopify Stores, WooCommerce & Payment Gateways"
    },
    {
      name: "Custom Enterprise Software",
      path: "/services/custom-enterprise-software",
      icon: "💻",
      desc: "Cloud Microservices, APIs & Internal Systems"
    },
    {
      name: "UI/UX & Logo Design",
      path: "/services/ui-ux-logo-design",
      icon: "✨",
      desc: "Figma Interfaces, Prototyping & Branding"
    }
  ];

  const mobileTabItems = [
    { name: "Home", path: "/", icon: <Home size={19} /> },
    { name: "Portfolio", path: "/Portfolio", icon: <Briefcase size={19} /> },
    { name: "Products", path: "/ourproducts/our-products-all", icon: <Boxes size={19} /> },
    { name: "Pricing", path: "/Pricing", icon: <CreditCard size={19} /> },
    { name: "Enquiry", path: "/enquirynow", icon: <Sparkles size={19} /> },
  ];


  useEffect(() => {
    setIsOpen(false);
    setIsEnquiryOpen(false);
    setIsServicesDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      const currentScrollY = window.scrollY || document.documentElement.scrollTop;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(currentScrollY > 20);
          if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
            setShowMobileTabs(false);
          } else {
            setShowMobileTabs(true);
          }
          lastScrollY.current = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen || isAiOpen || isEnquiryOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isOpen, isAiOpen, isEnquiryOpen]);

  const handleMouseEnterServices = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setIsServicesDropdownOpen(true);
  };

  const handleMouseLeaveServices = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsServicesDropdownOpen(false);
    }, 200);
  };

  const { menuItems } = useAdmin();

  const iconMap: Record<string, React.ReactNode> = {
    Home: <Home size={16} />,
    Info: <Info size={16} />,
    Boxes: <Boxes size={16} />,
    Layers: <Layers size={16} />,
    Briefcase: <Briefcase size={16} />,
    CreditCard: <CreditCard size={16} />,
    Calendar: <Layers3 size={16} />,
    Layers3: <Layers3 size={16} />,
    Send: <Send size={16} />
  };

  const navItems: NavItem[] = menuItems && menuItems.length > 0
    ? menuItems
        .filter((m) => m.isVisible)
        .sort((a, b) => a.order - b.order)
        .map((m) => ({
          name: m.name,
          path: m.path,
          icon: iconMap[m.iconName] || <Home size={16} />,
          hasDropdown: m.hasDropdown || m.path.toLowerCase() === "/services"
        }))
    : [
        { name: "Home", path: "/", icon: <Home size={16} /> },
        { name: "About", path: "/about", icon: <Info size={16} /> },
        { name: "Products", path: "/ourproducts/our-products-all", icon: <Boxes size={16} /> },
        { name: "Services", path: "/services", icon: <Layers size={16} />, hasDropdown: true },
        { name: "Portfolio", path: "/Portfolio", icon: <Briefcase size={16} /> },
        { name: "Pricing", path: "/Pricing", icon: <CreditCard size={16} /> },
        { name: "Meeting", path: "/Meeting", icon: <Layers3 size={16} /> },
        { name: "Contact", path: "/contact", icon: <Send size={16} /> }
      ];

  return (
    <div className="antialiased select-none font-sans pt-16">
      {/* MAIN NAVIGATION BAR */}
      <header
        className={`fixed left-0 top-0 w-full z-[110] transition-all duration-300 h-16 flex items-center ${
          shouldHideNavbarOnScroll ? "-translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100"
        } ${
          theme === "dark"
            ? isScrolled ? "bg-[#030712]/90 backdrop-blur-xl border-b border-white/10 shadow-2xl shadow-blue-950/20" : "bg-[#030712]/70 backdrop-blur-md border-b border-white/5"
            : isScrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-lg shadow-slate-200/50" : "bg-white/80 backdrop-blur-md border-b border-slate-100"
        }`}
      >
        <nav className="flex items-center justify-between w-full max-w-7xl mx-auto px-4 sm:px-6">
          {/* BRAND LOGO */}
          <Link to="/" className="relative z-[305] shrink-0 group flex items-center">
            <img src="/assets/logo/dl.webp" alt="Hickter Logo" className="h-8 sm:h-9 object-contain transition-transform group-hover:scale-105" />
          </Link>

          {/* DESKTOP NAV LINKS */}
          <div 
            className="hidden lg:flex items-center gap-1 bg-slate-100/90 dark:bg-slate-900/90 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-800 relative shadow-sm"
            onMouseLeave={() => setHoveredPath(null)}
          >
            {navItems.map((item) => {
              const isActive = location.pathname.toLowerCase().startsWith(item.path.toLowerCase()) && item.path !== "/" 
                ? true 
                : location.pathname.toLowerCase() === item.path.toLowerCase();
              const isHovered = hoveredPath === item.path;

              if (item.hasDropdown) {
                return (
                  <div 
                    key={item.path}
                    className="relative"
                    onMouseEnter={handleMouseEnterServices}
                    onMouseLeave={handleMouseLeaveServices}
                  >
                    <Link
                      to={item.path}
                      onMouseEnter={() => setHoveredPath(item.path)}
                      className={`relative px-3.5 py-1.5 text-xs font-bold tracking-wide transition-all duration-200 z-10 flex items-center gap-1 rounded-xl ${
                        isActive
                          ? "!text-white dark:!text-white font-extrabold bg-blue-600 dark:bg-blue-500 rounded-xl shadow-md"
                          : "text-slate-700 hover:text-slate-950 dark:text-slate-300 dark:hover:!text-white"
                      }`}
                    >
                      <span className={isActive ? "!text-white dark:!text-white font-extrabold" : ""}>{item.name}</span>
                      <ChevronDown size={14} className={`transition-transform duration-200 ${isActive ? "!text-white dark:!text-white" : ""} ${isServicesDropdownOpen ? "rotate-180" : ""}`} />
                    </Link>

                    {/* SERVICES DROPDOWN PANEL (DESKTOP) */}
                    <AnimatePresence>
                      {isServicesDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.98 }}
                          transition={{ duration: 0.18 }}
                          className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-80 sm:w-[480px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-3 z-[150] grid grid-cols-1 sm:grid-cols-2 gap-1.5"
                        >
                          {serviceSubmenu.map((sub, idx) => (
                            <Link
                              key={idx}
                              to={sub.path}
                              onClick={() => setIsServicesDropdownOpen(false)}
                              className="p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-all flex items-start gap-3 group/sub"
                            >
                              <span className="text-xl shrink-0 p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 group-hover/sub:scale-110 transition-transform">
                                {sub.icon}
                              </span>
                              <div className="space-y-0.5">
                                <h4 className="text-xs font-extrabold text-slate-900 dark:text-white group-hover/sub:text-blue-600 dark:group-hover/sub:text-cyan-400 transition-colors">
                                  {sub.name}
                                </h4>
                                <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium leading-tight">
                                  {sub.desc}
                                </p>
                              </div>
                            </Link>
                          ))}
                          
                          <div className="sm:col-span-2 pt-2 border-t border-slate-100 dark:border-slate-800/80 text-center">
                            <Link 
                              to="/services" 
                              onClick={() => setIsServicesDropdownOpen(false)}
                              className="text-[11px] font-bold text-blue-600 dark:text-cyan-400 hover:underline inline-flex items-center gap-1"
                            >
                              <span>Explore All Services Overview</span>
                              <ArrowRight size={12} />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onMouseEnter={() => setHoveredPath(item.path)}
                  className={`relative px-3.5 py-1.5 text-xs font-bold tracking-wide transition-all duration-200 z-10 flex items-center gap-1.5 rounded-xl ${
                    isActive
                      ? "!text-white dark:!text-white font-extrabold"
                      : "text-slate-700 hover:text-slate-950 dark:text-slate-300 dark:hover:!text-white"
                  }`}
                >
                  {isHovered && !isActive && (
                    <motion.div
                      layoutId="hoverBg"
                      className="absolute inset-0 bg-slate-200/80 dark:bg-slate-800/80 rounded-xl -z-10"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}

                  {isActive && (
                    <motion.div
                      layoutId="activeGlow"
                      className="absolute inset-0 bg-blue-600 dark:bg-blue-500 rounded-xl shadow-md shadow-blue-500/25 border border-blue-600 dark:border-blue-400 -z-10"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}

                  <span className={isActive ? "!text-white dark:!text-white font-extrabold" : ""}>{item.name}</span>
                </Link>
              );
            })}
          </div>

          {/* RIGHT ACTION BUTTONS + THEME TOGGLE (DESKTOP) */}
          <div className="hidden lg:flex items-center gap-3">
            {/* THEME TOGGLE BUTTON */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-cyan-400 transition-all cursor-pointer shadow-sm"
              title="Toggle Light/Dark Theme"
            >
              {theme === "dark" ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} className="text-slate-700" />}
            </button>

            <Link
              to="/chat"
              className="btn-outline px-4 py-2 text-xs font-bold rounded-xl flex items-center gap-2"
            >
              <Bot size={15} />
              <span>Chat</span>
            </Link>

            <Link
              to="/enquirynow"
              className="btn-primary-blue px-4 py-2 text-xs font-bold rounded-xl flex items-center gap-2 !text-white shadow-md shadow-blue-500/20"
            >
              <Sparkles size={15} className="!text-white" />
              <span className="!text-white">Enquiry</span>
            </Link>
          </div>

          {/* MOBILE TOGGLE & THEME BUTTON */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 cursor-pointer"
            >
              {theme === "dark" ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} className="text-slate-700" />}
            </button>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 cursor-pointer"
            >
              {isOpen ? <X size={20} /> : <div className="space-y-1 w-5"><span className="block h-0.5 w-5 bg-current"></span><span className="block h-0.5 w-5 bg-current"></span></div>}
            </button>
          </div>
        </nav>
      </header>

      {/* MOBILE FULL COVER MENU MODAL (Z-[400]) WITH FIXED ENQUIRY BUTTON */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[400] bg-white dark:bg-[#030712] lg:hidden flex flex-col justify-between overflow-hidden"
          >
            {/* DRAWER TOP HEADER BAR */}
            <div className="h-16 px-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
              <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center">
                <img src="/assets/logo/dl.webp" alt="Hickter Logo" className="h-8 object-contain" />
              </Link>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 cursor-pointer"
                >
                  {theme === "dark" ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} className="text-slate-700" />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-xl bg-red-50 dark:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30 cursor-pointer"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* DRAWER SCROLLABLE NAV LINKS CONTENT */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {navItems.map((item) => {
                const isActive = location.pathname.toLowerCase().startsWith(item.path.toLowerCase()) && item.path !== "/" 
                  ? true 
                  : location.pathname.toLowerCase() === item.path.toLowerCase();

                if (item.hasDropdown) {
                  return (
                    <div key={item.path} className="space-y-2">
                      <div 
                        onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                        className={`flex items-center justify-between p-3.5 rounded-2xl transition-all cursor-pointer ${
                          isActive
                            ? "bg-blue-600 !text-white dark:!text-white font-extrabold shadow-md"
                            : "bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-semibold"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {item.icon}
                          <span className={isActive ? "!text-white dark:!text-white font-extrabold" : ""}>{item.name}</span>
                        </div>
                        <ChevronDown size={16} className={`transition-transform ${isMobileServicesOpen ? "rotate-180" : ""}`} />
                      </div>

                      {/* MOBILE SUBMENU EXPANDABLE */}
                      {isMobileServicesOpen && (
                        <div className="pl-4 space-y-1.5 border-l-2 border-blue-500/30 ml-3">
                          {serviceSubmenu.map((sub, idx) => (
                            <Link
                              key={idx}
                              to={sub.path}
                              onClick={() => setIsOpen(false)}
                              className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50/80 dark:bg-slate-900/80 text-xs font-bold text-slate-800 dark:text-slate-200"
                            >
                              <span>{sub.icon}</span>
                              <span>{sub.name}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center justify-between p-3.5 rounded-2xl transition-all ${
                      isActive
                        ? "bg-blue-600 !text-white dark:!text-white font-extrabold shadow-md"
                        : "bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-semibold"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon}
                      <span className={isActive ? "!text-white dark:!text-white font-extrabold" : ""}>{item.name}</span>
                    </div>
                    <ArrowRight size={16} />
                  </Link>
                );
              })}
            </div>

            {/* DRAWER FIXED BOTTOM ENQUIRY BUTTON */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-[#030712] shrink-0">
              <Link
                to="/enquirynow"
                onClick={() => setIsOpen(false)}
                className="btn-primary-blue w-full h-12 text-sm font-bold rounded-xl flex items-center justify-center gap-2 !text-white shadow-lg shadow-blue-500/25"
              >
                <Sparkles size={18} className="!text-white" />
                <span className="!text-white">Enquiry Now</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MOBILE BOTTOM NAVIGATION TABS BAR (HIDDEN WHEN MOBILE MENU IS OPEN OR ON PRODUCT DETAIL PAGES) */}
      {!isOpen && !isProductDetailPage && (
        <div 
          className={`fixed bottom-0 left-0 right-0 z-[100] lg:hidden transition-transform duration-300 ${
            showMobileTabs ? "translate-y-0" : "translate-y-full pointer-events-none"
          } bg-white/95 dark:bg-[#030712]/95 backdrop-blur-xl border-t border-slate-200/80 dark:border-slate-800/80 px-2 py-1.5 shadow-2xl flex items-center justify-around`}
        >
          {mobileTabItems.map((tab) => {
            const isActive = location.pathname.toLowerCase().startsWith(tab.path.toLowerCase()) && tab.path !== "/"
              ? true
              : location.pathname.toLowerCase() === tab.path.toLowerCase();

            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded-xl transition-all ${
                  isActive
                    ? "text-blue-600 dark:text-cyan-400 font-extrabold"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium"
                }`}
              >
                <div className={`p-1 rounded-lg transition-transform ${isActive ? "scale-110 bg-blue-50 dark:bg-blue-500/15" : ""}`}>
                  {tab.icon}
                </div>
                <span className="text-[10px] tracking-tight leading-none">{tab.name}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
